// ============================================================
// DockerCapstone2.jsx
// ============================================================
// Junior-dev Notes:
// - Concept: Orchestrating an Enterprise Java Stack.
// - Stack: React + Spring Boot (JAR) + MySQL.
// - Strategy: Using 'depends_on' with healthchecks for DB readiness.
// - Branding: prodevconsult.com | Finnish Engineering Standards
// ============================================================

import React from "react";
import {
    Box,
    Container,
    Typography,
    Grid,
    Button,
    Stack,
    styled,
    Divider,
    Paper,
    Chip,
} from "@mui/material";
import { Link } from "react-router-dom";

/* Icons */
import CoffeeIcon from "@mui/icons-material/Coffee"; // Java Icon
import StorageIcon from "@mui/icons-material/Storage";
import LanIcon from "@mui/icons-material/Lan";
import TerminalIcon from "@mui/icons-material/Terminal";
import DnsIcon from "@mui/icons-material/Dns";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";

/* ── Brand tokens ────────────────────────────────────────── */
const DOCKER_BLUE = "#E5C95F"; // Gold
const NAVY_BG = "#100720";   // Neural Purple
const FINLAND_BLUE = "#003580";
const TEXT_WHITE = "#FFFFFF";

const BRAND = {
    muted: "rgba(255,255,255,0.7)",
    border: "rgba(229, 201, 95, 0.2)",
    domain: "prodevconsult.com"
};

/* ── Styled Components ───────────────────────────────────── */

const PageWrapper = styled(Box)(({ theme }) => ({
    minHeight: "100vh",
    backgroundColor: NAVY_BG,
    backgroundImage: `radial-gradient(circle at 2px 2px, rgba(255,255,255,0.05) 1px, transparent 0)`,
    backgroundSize: "40px 40px",
    color: TEXT_WHITE,
    paddingTop: theme.spacing(12),
    paddingBottom: theme.spacing(10),
}));

const ServiceCard = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(4),
    backgroundColor: "rgba(255,255,255,0.02)",
    border: `1px solid ${BRAND.border}`,
    borderRadius: "16px",
    height: "100%",
    "&:hover": {
        borderColor: DOCKER_BLUE,
        backgroundColor: "rgba(229, 201, 95, 0.05)",
    }
}));

const CodeSnippet = styled(Box)(({ theme }) => ({
    backgroundColor: "#011627",
    padding: theme.spacing(3),
    borderRadius: "12px",
    fontFamily: "'Fira Code', monospace",
    fontSize: "0.8rem",
    color: "#ADDB67", // Neon Green for code
    border: `1px solid ${BRAND.border}`,
    overflowX: "auto"
}));

const FinnishBadge = styled(Box)({
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    padding: "6px 12px",
    borderRadius: "4px",
    border: `1px solid rgba(255,255,255,0.1)`,
    backgroundColor: "rgba(255,255,255,0.03)",
    marginTop: "20px"
});

export default function DockerCapstone2() {
    return (
        <PageWrapper>
            <Container maxWidth="lg">

                {/* 1. HEADER SECTION */}
                <Box sx={{ mb: 8 }}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                        <Chip
                            label="Capstone II · Module 18"
                            sx={{ bgcolor: "rgba(229, 201, 95, 0.1)", color: DOCKER_BLUE, fontWeight: 700 }}
                        />
                        <Typography variant="caption" sx={{ color: DOCKER_BLUE, fontWeight: 800 }}>
                            {BRAND.domain.toUpperCase()}
                        </Typography>
                    </Stack>

                    <Typography variant="h2" sx={{ fontWeight: 900, mb: 2 }}>
                        Spring Boot & <span style={{ color: DOCKER_BLUE }}>MySQL Cluster</span>
                    </Typography>
                    <Typography variant="h6" sx={{ color: BRAND.muted, maxWidth: 850, lineHeight: 1.6 }}>
                        Master the deployment of enterprise Java applications. In this capstone,
                        you will containerize a Spring Boot Maven project, link it to a MySQL
                        instance, and handle database migrations automatically.
                    </Typography>

                    <FinnishBadge>
                        <Box sx={{ width: 18, height: 12, position: 'relative', bgcolor: '#fff' }}>
                            <Box sx={{ position: 'absolute', top: '40%', width: '100%', height: '20%', bgcolor: FINLAND_BLUE }} />
                            <Box sx={{ position: 'absolute', left: '30%', width: '15%', height: '100%', bgcolor: FINLAND_BLUE }} />
                        </Box>
                        <Typography variant="caption" sx={{ fontWeight: 800, color: "#fff", opacity: 0.8 }}>
                            FINNISH ENGINEERING STANDARDS
                        </Typography>
                    </FinnishBadge>
                </Box>

                {/* 2. THE ENTERPRISE STACK VISUAL */}
                <Typography variant="h4" sx={{ mb: 6, fontWeight: 800, textAlign: 'center' }}>
                    Enterprise <span style={{ color: DOCKER_BLUE }}>Stack Blueprint</span>
                </Typography>



                <Grid container spacing={4} sx={{ mb: 10 }}>
                    <Grid item xs={12} md={4}>
                        <ServiceCard>
                            <CoffeeIcon sx={{ color: DOCKER_BLUE, mb: 2, fontSize: 40 }} />
                            <Typography variant="h6" fontWeight={800} gutterBottom>Spring Boot API</Typography>
                            <Typography variant="body2" sx={{ color: BRAND.muted }}>
                                Multi-stage Maven build. Extracts the JAR file into a lightweight JRE
                                runtime for &lt; 150MB images.
                            </Typography>
                        </ServiceCard>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <ServiceCard>
                            <StorageIcon sx={{ color: DOCKER_BLUE, mb: 2, fontSize: 40 }} />
                            <Typography variant="h6" fontWeight={800} gutterBottom>MySQL Database</Typography>
                            <Typography variant="body2" sx={{ color: BRAND.muted }}>
                                Persistent storage for relational data. Configured with
                                custom root passwords and initialized schemas.
                            </Typography>
                        </ServiceCard>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <ServiceCard>
                            <DnsIcon sx={{ color: DOCKER_BLUE, mb: 2, fontSize: 40 }} />
                            <Typography variant="h6" fontWeight={800} gutterBottom>Internal DNS</Typography>
                            <Typography variant="body2" sx={{ color: BRAND.muted }}>
                                Spring Boot connects to MySQL via service name <code>jdbc:mysql://mysql-db:3306</code>
                                instead of localhost.
                            </Typography>
                        </ServiceCard>
                    </Grid>
                </Grid>

                {/* 3. CONFIGURATION CHALLENGE */}
                <Typography variant="h5" sx={{ mb: 4, fontWeight: 800 }}>
                    Docker <span style={{ color: DOCKER_BLUE }}>Environment Injection</span>
                </Typography>

                <Grid container spacing={4} sx={{ mb: 10 }}>
                    <Grid item xs={12} md={7}>
                        <CodeSnippet>
                            <Typography sx={{ color: "#82AAFF" }}># Spring Boot application.properties</Typography>
                            <Typography>spring.datasource.url=jdbc:mysql://$&#123;DB_HOST&#125;:3306/prodev_db</Typography>
                            <Typography>spring.datasource.username=$&#123;DB_USER&#125;</Typography>
                            <Typography>spring.datasource.password=$&#123;DB_PASS&#125;</Typography>
                            <Typography>spring.jpa.hibernate.ddl-auto=update</Typography>
                        </CodeSnippet>
                    </Grid>
                    <Grid item xs={12} md={5}>
                        <Paper sx={{ p: 4, bgcolor: "rgba(229, 201, 95, 0.05)", borderRadius: 4, border: `1px solid ${BRAND.border}` }}>
                            <Typography variant="h6" fontWeight={800} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                <VerifiedUserIcon sx={{ color: DOCKER_BLUE }} /> Dynamic Config
                            </Typography>
                            <Typography variant="body2" sx={{ color: BRAND.muted, lineHeight: 1.8 }}>
                                In professional SaaS, we never hardcode DB strings. We use <strong>Environment Variables</strong>
                                in Docker Compose to "inject" these values into the Java process at runtime.
                            </Typography>
                        </Paper>
                    </Grid>
                </Grid>

                {/* 4. THE PROJECT TASKS */}
                <Paper sx={{ p: 5, bgcolor: "rgba(255,255,255,0.02)", borderRadius: 4, border: `1px solid ${BRAND.border}` }}>
                    <Typography variant="h5" fontWeight={900} sx={{ color: DOCKER_BLUE, mb: 4, textAlign: 'center' }}>
                        Mission <span style={{ color: TEXT_WHITE }}>Checklist</span>
                    </Typography>
                    <Grid container spacing={3}>
                        {[
                            "Implement Maven Multi-stage Dockerfile (Build vs Run)",
                            "Setup MySQL container with health-check monitoring",
                            "Configure shared bridge network for backend and DB",
                            "Map host port 8080 to container port 8080",
                            "Initialize MySQL with a .sql script via /docker-entrypoint-initdb.d/"
                        ].map((task, i) => (
                            <Grid item xs={12} md={6} key={i}>
                                <Stack direction="row" spacing={2} alignItems="center">
                                    <TerminalIcon sx={{ color: DOCKER_BLUE, fontSize: 20 }} />
                                    <Typography variant="body2" fontWeight={600}>{task}</Typography>
                                </Stack>
                            </Grid>
                        ))}
                    </Grid>
                </Paper>

                <Divider sx={{ bgcolor: "rgba(255,255,255,0.05)", my: 8 }} />

                {/* 5. NAVIGATION */}
                <Box sx={{ textAlign: "center", py: 4 }}>
                    <Typography variant="h4" sx={{ mb: 4, fontWeight: 900 }}>Enterprise Ready.</Typography>
                    <Typography variant="body1" sx={{ color: BRAND.muted, mb: 6, maxWidth: 700, mx: 'auto' }}>
                        You've built the most requested stack in the software industry.
                        It's time to validate your knowledge through the final Docker Examination.
                    </Typography>
                    <Link to="/quizzes" style={{ textDecoration: "none" }}>
                        <Button
                            variant="contained"
                            size="large"
                            endIcon={<ArrowForwardIcon />}
                            sx={{
                                bgcolor: DOCKER_BLUE,
                                color: NAVY_BG,
                                fontWeight: 800,
                                px: 10,
                                py: 2.5,
                                boxShadow: `0 8px 24px rgba(229, 201, 95, 0.4)`,
                                "&:hover": { bgcolor: "#d4b950" }
                            }}
                        >
                            Take Final Exam
                        </Button>
                    </Link>
                </Box>

            </Container>
        </PageWrapper>
    );
}
